// ===== js/utils/helpers.js =====
/**
 * 工具函数模块
 */

/**
 * HTML 转义，防止 XSS 攻击
 */
function escapeHtml(unsafe) {
  if (typeof unsafe !== 'string') {
    return '';
  }
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/\//g, "&#x2F;");
}

/**
 * URL 转义
 */
function escapeUrl(url) {
  if (typeof url !== 'string') {
    return '#';
  }

  // 如果URL为空或只是空格，返回#
  if (!url || url.trim() === '') {
    return '#';
  }

  try {
    // 如果是锚链接，直接返回
    if (url.startsWith('#')) {
      return url;
    }

    // 验证 URL 格式
    const urlObj = new URL(url);
    return urlObj.toString();
  } catch (e) {
    // 静默处理无效URL，不显示警告
    return '#';
  }
}

/**
 * 防抖函数
 */
function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * 节流函数
 */
function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * 深拷贝对象
 */
function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item));
  }

  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
}

/**
 * 生成唯一 ID
 */
function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * 格式化文件大小
 */
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * 检测设备类型
 */
function getDeviceType() {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * 本地存储操作封装
 */
const Storage = {
  get(key, defaultValue = null) {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : defaultValue;
    } catch (e) {
      console.error('Storage get error:', e);
      return defaultValue;
    }
  },

  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      console.error('Storage set error:', e);
      return false;
    }
  },

  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.error('Storage remove error:', e);
      return false;
    }
  },

  clear() {
    try {
      localStorage.clear();
      return true;
    } catch (e) {
      console.error('Storage clear error:', e);
      return false;
    }
  }
};

/**
 * DOM 操作工具
 */
const DOM = {
  // 创建元素
  create(tag, attributes = {}, children = []) {
    const element = document.createElement(tag);

    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'className') {
        element.className = value;
      } else if (key === 'innerHTML') {
        element.innerHTML = value;
      } else if (key === 'textContent') {
        element.textContent = value;
      } else if (key.startsWith('data-') || key.startsWith('aria-')) {
        element.setAttribute(key, value);
      } else if (key === 'style' && typeof value === 'object') {
        Object.assign(element.style, value);
      } else if (key in element && typeof element[key] !== 'function') {
        element[key] = value;
      } else {
        element.setAttribute(key, value);
      }
    });

    children.forEach(child => {
      if (child === null || child === undefined) {
        return;
      }
      if (typeof child === 'string') {
        element.appendChild(document.createTextNode(child));
      } else if (child instanceof Node) {
        element.appendChild(child);
      } else if (Array.isArray(child)) {
        child.forEach(grandchild => {
          if (typeof grandchild === 'string') {
            element.appendChild(document.createTextNode(grandchild));
          } else if (grandchild instanceof Node) {
            element.appendChild(grandchild);
          }
        });
      }
    });

    return element;
  },

  // 查询元素
  find(selector, context = document) {
    return context.querySelector(selector);
  },

  findAll(selector, context = document) {
    return Array.from(context.querySelectorAll(selector));
  },

  // 添加事件监听
  on(element, event, handler, options = {}) {
    element.addEventListener(event, handler, options);
    return () => element.removeEventListener(event, handler, options);
  },

  // 移除元素
  remove(element) {
    if (element && element.parentNode) {
      element.parentNode.removeChild(element);
    }
  },

  // 显示/隐藏元素
  show(element) {
    element.style.display = '';
  },

  hide(element) {
    element.style.display = 'none';
  },

  // 切换显示状态
  toggle(element) {
    element.style.display = element.style.display === 'none' ? '' : 'none';
  }
};

/**
 * 动画工具
 */
const Animation = {
  // 淡入
  fadeIn(element, duration = 300) {
    element.style.opacity = 0;
    element.style.display = '';

    let start = null;
    function animate(timestamp) {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const opacity = Math.min(progress / duration, 1);

      element.style.opacity = opacity;

      if (progress < duration) {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  },

  // 淡出
  fadeOut(element, duration = 300, callback) {
    let start = null;
    const initialOpacity = parseFloat(window.getComputedStyle(element).opacity);

    function animate(timestamp) {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const opacity = Math.max(initialOpacity * (1 - progress / duration), 0);

      element.style.opacity = opacity;

      if (progress < duration) {
        requestAnimationFrame(animate);
      } else {
        element.style.display = 'none';
        if (callback) callback();
      }
    }

    requestAnimationFrame(animate);
  }
};

// 导出到全局
window.Helpers = {
  escapeHtml,
  escapeUrl,
  debounce,
  throttle,
  deepClone,
  generateId,
  formatFileSize,
  getDeviceType,
  Storage,
  DOM,
  Animation
};

// ===== js/utils/notification.js =====
/**
 * 通知系统模块
 * 提供用户友好的消息提示
 */
class NotificationManager {
  constructor() {
    this.container = null;
    this.notifications = [];
    this.maxNotifications = 5;
    this.defaultDuration = 4000;
    this.activeNotifications = new Set(); // 防止重复
    this.init();
  }

  init() {
    // 创建通知容器
    this.container = document.createElement('div');
    this.container.className = 'notification-container';
    this.container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      pointer-events: none;
    `;
    document.body.appendChild(this.container);
  }

  show(message, type = 'info', duration = this.defaultDuration) {
    try {
      // 防止空消息
      if (!message || typeof message !== 'string') {
        console.warn('Notification: Invalid message provided');
        return null;
      }

      // 创建唯一ID，避免重复
      const id = `${type}_${message}_${Date.now()}`;
      if (this.activeNotifications.has(id)) {
        console.log('Notification: Duplicate notification skipped:', message);
        return null;
      }

      const notification = this.createNotification(id, message, type);

      // 添加到容器
      this.container.appendChild(notification);
      this.notifications.push({ id, element: notification, type });
      this.activeNotifications.add(id);

      // 限制通知数量
      this.limitNotifications();

      // 显示动画
      requestAnimationFrame(() => {
        notification.classList.add('notification-show');
      });

      // 自动隐藏
      if (duration > 0) {
        setTimeout(() => {
          this.hide(id);
        }, duration);
      }

      return id;
    } catch (error) {
      console.error('Notification show error:', error);
      return null;
    }
  }

  hide(id) {
    try {
      const notificationIndex = this.notifications.findIndex(n => n.id === id);
      if (notificationIndex === -1) return;

      const { element } = this.notifications[notificationIndex];

      // 移除活跃状态
      this.activeNotifications.delete(id);

      element.classList.add('notification-hide');

      setTimeout(() => {
        try {
          if (element && element.parentNode) {
            element.parentNode.removeChild(element);
          }
          this.notifications.splice(notificationIndex, 1);
        } catch (error) {
          console.warn('Notification hide DOM removal error:', error);
        }
      }, 300);
    } catch (error) {
      console.error('Notification hide error:', error);
    }
  }

  createNotification(id, message, type) {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.dataset.id = id;

    // 图标映射
    const icons = {
      success: '✓',
      error: '✕',
      warning: '⚠',
      info: 'ℹ'
    };

    notification.innerHTML = `
      <div class="notification-icon">${icons[type] || icons.info}</div>
      <div class="notification-content">${message}</div>
      <button class="notification-close" onclick="NotificationUtils.hideStatic(${id})">×</button>
    `;

    // 添加样式
    this.addStyles();

    return notification;
  }

  addStyles() {
    // 如果样式已添加，跳过
    if (document.getElementById('notification-styles')) {
      return;
    }

    const styles = document.createElement('style');
    styles.id = 'notification-styles';
    styles.textContent = `
      .notification-container {
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-width: 400px;
      }

      .notification {
        background: rgba(43, 43, 43, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 16px;
        color: #ffffff;
        font-size: 14px;
        line-height: 1.4;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        backdrop-filter: blur(10px);
        display: flex;
        align-items: center;
        gap: 12px;
        pointer-events: all;
        transform: translateX(100%);
        opacity: 0;
        transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      }

      .notification-show {
        transform: translateX(0);
        opacity: 1;
      }

      .notification-hide {
        transform: translateX(100%);
        opacity: 0;
      }

      .notification-icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 12px;
      }

      .notification-success .notification-icon {
        background: #28a745;
        color: white;
      }

      .notification-error .notification-icon {
        background: #dc3545;
        color: white;
      }

      .notification-warning .notification-icon {
        background: #ffc107;
        color: #212529;
      }

      .notification-info .notification-icon {
        background: #17a2b8;
        color: white;
      }

      .notification-content {
        flex: 1;
        word-wrap: break-word;
      }

      .notification-close {
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.7);
        cursor: pointer;
        font-size: 18px;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.2s;
        flex-shrink: 0;
      }

      .notification-close:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }

      /* 响应式设计 */
      @media (max-width: 768px) {
        .notification-container {
          right: 10px;
          left: 10px;
          max-width: none;
        }

        .notification {
          font-size: 13px;
          padding: 12px;
        }
      }

      /* 深色模式适配 */
      @media (prefers-color-scheme: light) {
        .notification {
          background: rgba(255, 255, 255, 0.95);
          color: #333333;
          border-color: rgba(0, 0, 0, 0.1);
        }

        .notification-close {
          color: rgba(0, 0, 0, 0.6);
        }

        .notification-close:hover {
          background: rgba(0, 0, 0, 0.1);
          color: #333333;
        }
      }
    `;

    document.head.appendChild(styles);
  }

  limitNotifications() {
    if (this.notifications.length > this.maxNotifications) {
      const oldestNotification = this.notifications.shift();
      this.hide(oldestNotification.id);
    }
  }

  // 清除所有通知
  clear() {
    this.notifications.forEach(({ id }) => {
      this.hide(id);
    });
  }

  // 成功通知
  success(message, duration) {
    return this.show(message, 'success', duration);
  }

  // 错误通知
  error(message, duration) {
    return this.show(message, 'error', duration);
  }

  // 警告通知
  warning(message, duration) {
    return this.show(message, 'warning', duration);
  }

  // 信息通知
  info(message, duration) {
    return this.show(message, 'info', duration);
  }
}

// 静默模式 - 禁用所有通知
let silentMode = true;

// 创建全局实例，使用不同的名称避免冲突
const notificationInstance = new NotificationManager();

// 将实例赋值给全局变量
window.NotificationUtils = notificationInstance;

// 重新定义静态方法，直接操作实例
window.NotificationUtils.showSuccess = (message, duration) => {
  if (silentMode) return null;
  try {
    return notificationInstance.success(message, duration);
  } catch (error) {
    console.error('NotificationUtils.showSuccess error:', error);
    return null;
  }
};

window.NotificationUtils.showError = (message, duration) => {
  if (silentMode) return null;
  try {
    return notificationInstance.error(message, duration);
  } catch (error) {
    console.error('NotificationUtils.showError error:', error);
    return null;
  }
};

window.NotificationUtils.showWarning = (message, duration) => {
  if (silentMode) return null;
  try {
    return notificationInstance.warning(message, duration);
  } catch (error) {
    console.error('NotificationUtils.showWarning error:', error);
    return null;
  }
};

window.NotificationUtils.showInfo = (message, duration) => {
  if (silentMode) return null;
  try {
    return notificationInstance.info(message, duration);
  } catch (error) {
    console.error('NotificationUtils.showInfo error:', error);
    return null;
  }
};

// 修复无限递归 - 直接调用实例方法
window.NotificationUtils.hideStatic = (id) => {
  try {
    return notificationInstance.hide(id);
  } catch (error) {
    console.error('NotificationUtils.hide error:', error);
    return null;
  }
};

window.NotificationUtils.clearStatic = () => {
  try {
    return notificationInstance.clear();
  } catch (error) {
    console.error('NotificationUtils.clear error:', error);
    return null;
  }
};

// 提供启用/禁用静默模式的方法
window.NotificationUtils.setSilentMode = (silent) => {
  silentMode = silent;
};

window.NotificationUtils.isSilentMode = () => {
  return silentMode;
};

// ===== js/utils/module-loader.js =====
/**
 * 模块加载器
 * 确保所有模块按正确顺序加载
 */

class ModuleLoader {
  constructor() {
    this.modules = {
      // 工具模块 - 无依赖
      'helpers': { loaded: false, file: 'js/utils/helpers.js', deps: [] },
      'notification': { loaded: false, file: 'js/utils/notification.js', deps: ['helpers'] },
      'service-worker': { loaded: false, file: 'js/utils/service-worker.js', deps: ['helpers'] },
      'image-optimizer': { loaded: false, file: 'js/utils/image-optimizer.js', deps: ['helpers', 'notification'] },

      // 核心模块 - 依赖工具模块
      'config-manager': { loaded: false, file: 'js/modules/config-manager.js', deps: ['helpers', 'notification'] },
      'app-renderer': { loaded: false, file: 'js/modules/app-renderer.js', deps: ['helpers', 'notification', 'config-manager'] },
      'ui-controller': { loaded: false, file: 'js/modules/ui-controller.js', deps: ['helpers', 'notification', 'config-manager', 'app-renderer'] },
      'search-engine': { loaded: false, file: 'js/modules/search-engine.js', deps: ['helpers', 'notification', 'config-manager', 'image-optimizer'] }
    };

    this.globalNames = {
      'helpers': 'Helpers',
      'notification': 'NotificationUtils',
      'service-worker': 'ServiceWorkerManager',
      'image-optimizer': 'ImageOptimizer',
      'config-manager': 'ConfigManager',
      'app-renderer': 'AppRenderer',
      'ui-controller': 'UIManager',
      'search-engine': 'SearchEngine'
    };

    this.loadedModules = new Set();
    this.loadPromises = new Map();
    this.init();
  }

  init() {
    // 标记已通过script标签加载的模块
    this.checkLoadedModules();
  }

  checkLoadedModules() {
    Object.keys(this.globalNames).forEach(moduleKey => {
      const globalName = this.globalNames[moduleKey];
      if (typeof window[globalName] !== 'undefined') {
        this.modules[moduleKey].loaded = true;
        this.loadedModules.add(moduleKey);
        console.log(`✅ 模块已加载: ${moduleKey} (${globalName})`);
      }
    });
  }

  async loadModule(moduleKey) {
    if (this.loadedModules.has(moduleKey)) {
      return true;
    }

    if (this.loadPromises.has(moduleKey)) {
      return await this.loadPromises.get(moduleKey);
    }

    const module = this.modules[moduleKey];
    if (!module) {
      throw new Error(`未知模块: ${moduleKey}`);
    }

    const loadPromise = this._loadModuleDependencies(moduleKey);
    this.loadPromises.set(moduleKey, loadPromise);

    try {
      const result = await loadPromise;
      console.log(`📦 模块加载完成: ${moduleKey}`);
      return result;
    } catch (error) {
      console.error(`❌ 模块加载失败: ${moduleKey}`, error);
      throw error;
    } finally {
      this.loadPromises.delete(moduleKey);
    }
  }

  async _loadModuleDependencies(moduleKey) {
    const module = this.modules[moduleKey];

    // 先加载依赖
    for (const depKey of module.deps) {
      await this.loadModule(depKey);
    }

    // 检查模块是否已加载
    this.checkLoadedModules();
    if (this.loadedModules.has(moduleKey)) {
      return true;
    }

    // 动态加载模块
    return await this._loadScript(moduleKey, module.file);
  }

  _loadScript(moduleKey, url) {
    return new Promise((resolve, reject) => {
      // 检查是否已经在加载中
      if (document.querySelector(`script[data-module="${moduleKey}"]`)) {
        reject(new Error(`模块 ${moduleKey} 已在加载中`));
        return;
      }

      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.module = moduleKey;

      script.onload = () => {
        // 等待一小段时间确保模块初始化完成
        setTimeout(() => {
          const globalName = this.globalNames[moduleKey];
          if (typeof window[globalName] !== 'undefined') {
            this.modules[moduleKey].loaded = true;
            this.loadedModules.add(moduleKey);
            console.log(`✅ 模块加载成功: ${moduleKey} -> ${globalName}`);
            resolve(true);
          } else {
            reject(new Error(`模块 ${moduleKey} 加载后未找到全局对象 ${globalName}`));
          }
        }, 100);
      };

      script.onerror = () => {
        reject(new Error(`模块 ${moduleKey} 加载失败: ${url}`));
      };

      document.head.appendChild(script);
    });
  }

  async loadAllModules() {
    console.log('🚀 开始加载所有模块...');

    try {
      for (const moduleKey of Object.keys(this.modules)) {
        await this.loadModule(moduleKey);
      }

      console.log('🎉 所有模块加载完成！');
      return true;
    } catch (error) {
      console.error('❌ 模块加载失败:', error);
      throw error;
    }
  }

  async waitForModules(moduleKeys, timeout = 10000) {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      this.checkLoadedModules();

      const allLoaded = moduleKeys.every(key => this.loadedModules.has(key));
      if (allLoaded) {
        return true;
      }

      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const missing = moduleKeys.filter(key => !this.loadedModules.has(key));
    throw new Error(`等待模块超时: ${missing.join(', ')}`);
  }

  getLoadedModules() {
    this.checkLoadedModules();
    return Array.from(this.loadedModules);
  }

  getMissingModules() {
    this.checkLoadedModules();
    return Object.keys(this.modules).filter(key => !this.loadedModules.has(key));
  }

  getModuleInfo() {
    this.checkLoadedModules();
    const info = {};

    Object.keys(this.modules).forEach(key => {
      const module = this.modules[key];
      const globalName = this.globalNames[key];

      info[key] = {
        loaded: this.loadedModules.has(key),
        globalAvailable: typeof window[globalName] !== 'undefined',
        file: module.file,
        deps: module.deps
      };
    });

    return info;
  }
}

// 创建全局模块加载器实例
window.ModuleLoader = new ModuleLoader();

// ===== js/modules/config-manager.js =====
/**
 * 配置管理模块
 * 负责应用配置的加载、保存和同步
 */
class ConfigManager {
  constructor() {
    this.CONFIG_KEY = 'homedock-config';
    this.API_ENDPOINT = this.resolveApiEndpoint();
    this.cachedConfig = null;
  }

  resolveApiEndpoint() {
    try {
      const { origin, hostname } = window.location || {};

      // 当托管在 Cloudflare Pages（*.pages.dev）时，指向已部署的 workers.dev 域名
      if (hostname && hostname.endsWith('.pages.dev')) {
        return 'https://homedock.piaozhitian.workers.dev/api/config';
      }

      // 默认：相对当前站点的 /api/config（适用于本地 dev-server 和自定义域）
      if (origin && origin.startsWith('http')) {
        return origin.replace(/\/+$/, '') + '/api/config';
      }

      return '/api/config';
    } catch (e) {
      console.warn('API 端点解析失败，回退到默认 /api/config', e);
      return '/api/config';
    }
  }

  /**
   * 带重试机制的配置加载
   */
  async loadConfigWithRetry(maxRetries = 3) {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const config = await this.loadConfigFromServer();
        if (config && this.validateConfig(config)) {
          this.cachedConfig = config;
          localStorage.setItem(this.CONFIG_KEY, JSON.stringify(config));
          return config;
        }
      } catch (error) {
        console.warn(`配置加载尝试 ${i + 1} 失败:`, error);
        if (i === maxRetries - 1) {
          NotificationUtils.showWarning('配置加载失败，使用本地缓存');
        }
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }

    // 回退到本地配置
    return this.loadLocalConfig();
  }

  /**
   * 从服务器加载配置
   */
  async loadConfigFromServer() {
    try {
      const response = await fetch(this.API_ENDPOINT, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // 服务器返回 null 表示 KV 中没有配置，回退到本地配置
      if (data === null) {
        console.info('服务器返回 null，回退到本地配置');
        return null;
      }

      if (!this.validateConfig(data)) {
        throw new Error('服务器返回的配置格式无效');
      }

      return this.normalizeConfig(data);
    } catch (error) {
      console.error('从服务器加载配置失败:', error);
      return null;
    }
  }

  /**
   * 加载本地配置
   */
  loadLocalConfig() {
    try {
      const localConfig = JSON.parse(localStorage.getItem(this.CONFIG_KEY));

      if (localConfig && this.validateConfig(localConfig)) {
        this.cachedConfig = localConfig;
        return localConfig;
      }

      // 如果本地没有有效配置，加载默认配置
      return this.loadDefaultConfig();
    } catch (error) {
      console.error('加载本地配置失败:', error);
      return this.loadDefaultConfig();
    }
  }

  /**
   * 加载默认配置
   */
  async loadDefaultConfig() {
    try {
      const response = await fetch('apps-config.json');
      const defaultConfig = await response.json();
      const normalizedConfig = this.normalizeConfig(defaultConfig);

      this.cachedConfig = normalizedConfig;
      localStorage.setItem(this.CONFIG_KEY, JSON.stringify(normalizedConfig));

      return normalizedConfig;
    } catch (error) {
      console.error('加载默认配置失败:', error);
      NotificationUtils.showError('配置加载失败，请刷新页面重试');
      return null;
    }
  }

  /**
   * 保存配置到本地和服务器
   */
  async saveConfig(config) {
    if (!this.validateConfig(config)) {
      throw new Error('配置格式无效');
    }

    // 保存到本地存储
    localStorage.setItem(this.CONFIG_KEY, JSON.stringify(config));
    this.cachedConfig = config;

    // 尝试保存到服务器
    try {
      await this.saveConfigToServer(config);
      NotificationUtils.showSuccess('配置已保存');
    } catch (error) {
      console.warn('保存到服务器失败，仅保存到本地:', error);
      NotificationUtils.showWarning('配置已保存到本地');
    }
  }

  /**
   * 保存配置到服务器
   */
  async saveConfigToServer(config) {
    const response = await fetch(this.API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(config)
    });

    if (!response.ok) {
      throw new Error(`保存失败: ${response.status}`);
    }
  }

  /**
   * 验证配置格式
   */
  validateConfig(config) {
    return config &&
           typeof config === 'object' &&
           Array.isArray(config.applications) &&
           config.applications.every(app =>
             typeof app.name === 'string' &&
             app.name.trim().length > 0
           );
  }

  /**
   * 标准化配置格式
   */
  normalizeConfig(config) {
    if (!config.background) {
      config.background = {
        mode: 'wallpaper',
        solidColor: '#202124',
        gradientFrom: '#141e30',
        gradientTo: '#243b55'
      };
    }

    // 确保所有应用都有必要的字段
    config.applications = config.applications.map(app => ({
      name: app.name || '未命名应用',
      external_url: app.external_url || '',
      internal_url: app.internal_url || '',
      icon: app.icon || 'img/png/favicon.svg',
      ...app
    }));

    return config;
  }

  /**
   * 获取当前配置
   */
  getCurrentConfig() {
    return this.cachedConfig || this.loadLocalConfig();
  }

  /**
   * 添加应用
   */
  addApplication(app) {
    const config = this.getCurrentConfig();
    config.applications.push(app);
    return this.saveConfig(config);
  }

  /**
   * 更新应用
   */
  updateApplication(index, app) {
    const config = this.getCurrentConfig();
    if (config.applications[index]) {
      config.applications[index] = { ...config.applications[index], ...app };
      return this.saveConfig(config);
    }
    throw new Error('应用不存在');
  }

  /**
   * 删除应用
   */
  deleteApplication(index) {
    const config = this.getCurrentConfig();
    if (config.applications[index]) {
      config.applications.splice(index, 1);
      return this.saveConfig(config);
    }
    throw new Error('应用不存在');
  }

  /**
   * 重新排序应用
   */
  reorderApplications(fromIndex, toIndex) {
    const config = this.getCurrentConfig();
    const [movedApp] = config.applications.splice(fromIndex, 1);
    config.applications.splice(toIndex, 0, movedApp);
    return this.saveConfig(config);
  }

  /**
   * 更新背景设置
   */
  updateBackground(background) {
    const config = this.getCurrentConfig();
    config.background = { ...config.background, ...background };
    return this.saveConfig(config);
  }
}

// 导出单例实例
window.ConfigManager = new ConfigManager();


// ===== js/modules/app-renderer.js =====
/**
 * 应用渲染器模块
 * 负责应用的渲染和交互
 */
class AppRenderer {
  constructor() {
    this.APPS_PER_PAGE = 15;  // ✅ 改为15个（3行×5列）
    this.externalPageIndex = 0;
    this.internalPageIndex = 0;
    this.editMode = false;
    this.configManager = window.ConfigManager;
    this.init();
  }

  init() {
    // 等待DOM加载完成
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // 编辑切换按钮
    const editToggleBtn = DOM.find('#edit-toggle-btn');
    if (editToggleBtn) {
      DOM.on(editToggleBtn, 'click', (e) => {
        e.stopPropagation();
        this.toggleEditMode();
      });
    }

    // 分页控制
    this.setupPagingControls();

    // 点击空白区域退出编辑模式
    DOM.on(document, 'click', (e) => {
      if (this.editMode &&
          !e.target.closest('.app-item') &&
          !e.target.closest('.edit-modal') &&
          !e.target.closest('#edit-toggle-btn')) {
        this.exitEditMode();
      }
    });

    // 键盘快捷键
    DOM.on(document, 'keydown', (e) => {
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        this.toggleEditMode();
      }
    });
  }

  /**
   * 渲染外部应用
   */
  renderExternalApps() {
    const appsList = DOM.find('#external-apps');
    const nextBtn = DOM.find('#external-next-btn');

    if (!appsList) {
      console.warn('⚠️ #external-apps container not found');
      return;
    }

    appsList.innerHTML = '';

    let config = this.configManager.getCurrentConfig();
    if (!config || typeof config.then === 'function' || !Array.isArray(config.applications)) {
      console.warn('⚠️ No valid config found, reloading config...');
      this.configManager.loadConfigWithRetry().then(() => {
        this.renderExternalApps();
        this.renderInternalApps();
      }).catch((error) => {
        console.error('❌ Failed to reload config:', error);
      });
      return;
    }

    // 过滤外部应用 - 只显示有有效URL的应用
    const appsWithIndex = config.applications
      .map((app, index) => ({ app, index }))
      .filter(item => item.app.external_url && item.app.external_url.trim() && item.app.external_url !== '#');

    
    this.renderApps(appsList, appsWithIndex, 'external', this.externalPageIndex);

    // 更新分页按钮状态
    if (nextBtn) {
      const total = appsWithIndex.length;
      nextBtn.classList.toggle('page-arrow-hidden', total <= this.APPS_PER_PAGE);
    }
  }

  /**
   * 渲染内部应用
   */
  renderInternalApps() {
    const appsList = DOM.find('#internal-apps');
    const nextBtn = DOM.find('#internal-next-btn');

    if (!appsList) {
      console.warn('⚠️ #internal-apps container not found');
      return;
    }

    appsList.innerHTML = '';

    let config = this.configManager.getCurrentConfig();
    if (!config || typeof config.then === 'function' || !Array.isArray(config.applications)) {
      console.warn('⚠️ No valid config found for internal apps, reloading config...');
      this.configManager.loadConfigWithRetry().then(() => {
        this.renderInternalApps();
      }).catch((error) => {
        console.error('❌ Failed to reload config for internal apps:', error);
      });
      return;
    }

    // 过滤内部应用 - 只显示有有效URL的应用
    const appsWithIndex = config.applications
      .map((app, index) => ({ app, index }))
      .filter(item => item.app.internal_url && item.app.internal_url.trim() && item.app.internal_url !== '#');

    
    this.renderApps(appsList, appsWithIndex, 'internal', this.internalPageIndex);

    // 更新分页按钮状态
    if (nextBtn) {
      const total = appsWithIndex.length;
      nextBtn.classList.toggle('page-arrow-hidden', total <= this.APPS_PER_PAGE);
    }
  }

  /**
   * 通用应用渲染方法
   */
  renderApps(container, appsWithIndex, type, pageIndex) {
    const total = appsWithIndex.length;
    const maxPage = Math.max(0, Math.ceil(total / this.APPS_PER_PAGE) - 1);

    // 调整页面索引
    if (pageIndex > maxPage) {
      pageIndex = 0;
      if (type === 'external') {
        this.externalPageIndex = 0;
      } else {
        this.internalPageIndex = 0;
      }
    }

    const start = pageIndex * this.APPS_PER_PAGE;
    const pageItems = appsWithIndex.slice(start, start + this.APPS_PER_PAGE);

    // 渲染应用项
    pageItems.forEach(({ app, index }) => {
      const li = this.createAppItem(app, index, type);
      container.appendChild(li);
    });

    // 添加"添加应用"按钮
    const addLi = this.createAddAppItem(type);
    container.appendChild(addLi);

    // 添加拖拽支持
    this.enableDragAndDrop(container, type);
  }

  /**
   * 创建应用项
   */
  createAppItem(app, index, type) {
    const li = DOM.create('li', {
      className: 'app-page-item',
      draggable: true,
      'data-app-index': index,
      'data-app-type': type
    });

    const url = type === 'external' ? app.external_url : app.internal_url;
    const iconSrc = app.icon && app.icon.trim() ? app.icon : 'img/png/favicon.svg';
    const target = url && url.startsWith('#') ? '_self' : '_blank';

    const appItemContent = DOM.create('div', {
      className: 'app-item',
      'data-app-index': index
    }, [
      DOM.create('a', {
        'data-url': Helpers.escapeUrl(url),  /* 存储URL在data属性中 */
        'data-target': target,
        role: 'button',  /* 语义化：这是一个按钮 */
        tabindex: '0'  /* 可通过键盘聚焦 */
      }, [
        DOM.create('img', {
          className: 'shake',
          src: iconSrc,
          alt: Helpers.escapeHtml(app.name),
          onerror: 'this.onerror=null;this.src=\'img/png/favicon.svg\''
        }),
        DOM.create('strong', {}, [Helpers.escapeHtml(app.name)])
      ]),
      this.createEditButtons(index)
    ]);

    // 添加点击事件处理导航
    const link = appItemContent.querySelector('a');
    if (link) {
      DOM.on(link, 'click', (e) => {
        e.preventDefault();
        const url = link.getAttribute('data-url');
        const target = link.getAttribute('data-target') || '_blank';
        if (url && url !== '#' && url.trim() !== '') {
          if (target === '_blank') {
            window.open(url, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = url;
          }
        }
      });
    }

    // 添加右键菜单事件
    DOM.on(appItemContent, 'contextmenu', (e) => {
      e.preventDefault();
      this.enterEditMode();
    });

    li.appendChild(appItemContent);
    return li;
  }

  /**
   * 创建编辑按钮
   */
  createEditButtons(index) {
    // 创建编辑按钮 SVG 图标
    const editBtnIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    editBtnIcon.setAttribute('class', 'edit-icon-svg');
    editBtnIcon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    editBtnIcon.setAttribute('width', '16');
    editBtnIcon.setAttribute('height', '16');
    editBtnIcon.setAttribute('viewBox', '0 0 24 24');
    editBtnIcon.setAttribute('fill', 'none');
    editBtnIcon.setAttribute('stroke', 'currentColor');
    editBtnIcon.setAttribute('stroke-width', '2');
    editBtnIcon.setAttribute('stroke-linecap', 'round');
    editBtnIcon.setAttribute('stroke-linejoin', 'round');
    editBtnIcon.innerHTML = '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>';

    const editBtn = DOM.create('button', {
      className: 'edit-btn icon-btn',
      style: 'display: none;'
    }, []);
    editBtn.appendChild(editBtnIcon);

    const deleteBtn = DOM.create('button', {
      className: 'icon-btn delete-btn',
      style: 'display: none;'
    });

    // 添加事件监听器而不是使用onclick属性
    DOM.on(editBtn, 'click', (e) => {
      e.stopPropagation();
      window.UIManager.editApp(index);
    });

    DOM.on(deleteBtn, 'click', (e) => {
      e.stopPropagation();
      window.UIManager.deleteApp(index);
    });

    return [editBtn, deleteBtn];
  }

  /**
   * 创建添加应用项
   */
  createAddAppItem(type) {
    const li = DOM.create('li', { className: 'app-page-item add-app-item' });

    const addItem = DOM.create('div', {
      className: 'app-item add-app-item-inner'
    }, [
      DOM.create('strong', {}, ['+ 添加应用'])
    ]);

    DOM.on(addItem, 'click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (window.UIManager && typeof window.UIManager.openAddApp === 'function') {
        window.UIManager.openAddApp(type);
      }
    });

    li.appendChild(addItem);
    return li;
  }

  /**
   * 启用拖拽排序
   */
  enableDragAndDrop(container, type) {
    let draggedElement = null;
    let draggedIndex = null;
    let currentOverElement = null;

    const resetDragState = () => {
      if (draggedElement) {
        draggedElement.classList.remove('dragging');
      }
      if (currentOverElement) {
        currentOverElement.classList.remove('drag-over');
      }
      draggedElement = null;
      draggedIndex = null;
      currentOverElement = null;
    };

    DOM.on(container, 'dragstart', (e) => {
      const item = e.target.closest('.app-page-item');
      if (!item || item.classList.contains('add-app-item')) return;

      draggedElement = item;
      draggedIndex = parseInt(draggedElement.dataset.appIndex);
      draggedElement.classList.add('dragging');

      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', '');
      }
    });

    DOM.on(container, 'dragover', (e) => {
      e.preventDefault();
      if (!draggedElement) return;
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }

      const target = e.target.closest('.app-page-item');
      if (!target || target === draggedElement || target.classList.contains('add-app-item')) {
        return;
      }

      if (currentOverElement && currentOverElement !== target) {
        currentOverElement.classList.remove('drag-over');
      }
      target.classList.add('drag-over');
      currentOverElement = target;

      const rect = target.getBoundingClientRect();
      const offsetY = e.clientY - rect.top;
      const shouldInsertBefore = offsetY < rect.height / 2;

      if (shouldInsertBefore) {
        container.insertBefore(draggedElement, target);
      } else {
        container.insertBefore(draggedElement, target.nextSibling);
      }
    });

    DOM.on(container, 'drop', async (e) => {
      e.preventDefault();
      if (draggedElement) {
        draggedElement.classList.remove('dragging');
        if (currentOverElement) {
          currentOverElement.classList.remove('drag-over');
        }

        // 获取新的顺序
        const items = Array.from(container.querySelectorAll('.app-page-item:not(.add-app-item)'));
        const newIndex = items.indexOf(draggedElement);

        if (newIndex !== draggedIndex) {
          try {
            await this.configManager.reorderApplications(draggedIndex, newIndex);
            NotificationUtils.showSuccess('应用顺序已更新');

            // 重新渲染
            if (type === 'external') {
              this.renderExternalApps();
            } else {
              this.renderInternalApps();
            }
          } catch (error) {
            NotificationUtils.showError('更新顺序失败: ' + error.message);
          }
        }
      }
    });

    DOM.on(container, 'dragend', () => {
      resetDragState();
    });

    DOM.on(container, 'dragleave', (e) => {
      const item = e.target.closest('.app-page-item');
      if (item) {
        item.classList.remove('drag-over');
      }
    });

    DOM.on(window, 'mouseup', () => {
      if (draggedElement) {
        resetDragState();
      }
    });
  }

  /**
   * 获取拖拽后的位置
   */
  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll('.app-page-item:not(.dragging):not(.add-app-item)')];

    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;

      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child };
      } else {
        return closest;
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element;
  }

  /**
   * 设置分页控制
   */
  setupPagingControls() {
    const externalNext = DOM.find('#external-next-btn');
    const internalNext = DOM.find('#internal-next-btn');

    if (externalNext) {
      DOM.on(externalNext, 'click', () => {
        this.nextPage('external');
      });
    }

    if (internalNext) {
      DOM.on(internalNext, 'click', () => {
        this.nextPage('internal');
      });
    }
  }

  /**
   * 下一页
   */
  nextPage(type) {
    const config = this.configManager.getCurrentConfig();
    if (!config || !Array.isArray(config.applications)) return;

    const apps = config.applications.filter(app =>
      type === 'external' ? app.external_url : app.internal_url
    );

    const maxPage = Math.max(0, Math.ceil(apps.length / this.APPS_PER_PAGE) - 1);

    if (type === 'external') {
      this.externalPageIndex = this.externalPageIndex >= maxPage ? 0 : this.externalPageIndex + 1;
      this.renderExternalApps();
    } else {
      this.internalPageIndex = this.internalPageIndex >= maxPage ? 0 : this.internalPageIndex + 1;
      this.renderInternalApps();
    }

    if (this.editMode) {
      this.addRightClickEdit();
    }
  }

  /**
   * 切换编辑模式
   */
  toggleEditMode() {
    if (this.editMode) {
      this.exitEditMode();
    } else {
      this.enterEditMode();
    }
  }

  /**
   * 进入编辑模式
   */
  enterEditMode() {
    this.editMode = true;
    document.body.classList.add('edit-mode');

    // 显示删除按钮（编辑按钮由CSS控制悬浮时显示）
    DOM.findAll('.delete-btn').forEach(btn => {
      btn.style.display = 'flex';
    });

    // 移除原有动画，添加编辑模式动画
    DOM.findAll('.shake, .edit-shake').forEach(element => {
      element.classList.remove('shake', 'edit-shake');
    });

    DOM.findAll('.app-item img').forEach(img => {
      img.classList.add('edit-shake');
    });

    DOM.findAll('.edit-btn').forEach(btn => {
      btn.classList.add('edit-shake');
    });

    // 更新按钮状态
    const toggleBtn = DOM.find('#edit-toggle-btn');
    const editText = DOM.find('.edit-text');
    if (toggleBtn && editText) {
      toggleBtn.classList.add('editing');
      toggleBtn.setAttribute('aria-pressed', 'true');  // ✅ 更新 ARIA 状态
      editText.textContent = '完成';
    }

    // 添加右键编辑支持
    this.addRightClickEdit();
  }

  /**
   * 退出编辑模式
   */
  exitEditMode() {
    this.editMode = false;
    document.body.classList.remove('edit-mode');

    // 隐藏编辑按钮
    DOM.findAll('.icon-btn').forEach(btn => {
      btn.style.display = 'none';
    });

    // 恢复默认动画
    DOM.findAll('.edit-shake').forEach(element => {
      element.classList.remove('edit-shake');
    });

    DOM.findAll('.app-item img').forEach(img => {
      img.classList.add('shake');
    });

    // 更新按钮状态
    const toggleBtn = DOM.find('#edit-toggle-btn');
    const editText = DOM.find('.edit-text');
    if (toggleBtn && editText) {
      toggleBtn.classList.remove('editing');
      toggleBtn.setAttribute('aria-pressed', 'false');  // ✅ 更新 ARIA 状态
      editText.textContent = '编辑';
    }
  }

  /**
   * 添加右键编辑支持
   */
  addRightClickEdit() {
    DOM.findAll('.app-item a').forEach(link => {
      DOM.on(link, 'contextmenu', (e) => {
        e.preventDefault();

        if (!this.editMode) {
          this.enterEditMode();
        }
      });
    });
  }

  /**
   * 刷新所有应用
   */
  refresh() {
    this.externalPageIndex = 0;
    this.internalPageIndex = 0;
    this.renderExternalApps();
    this.renderInternalApps();

    if (this.editMode) {
      this.addRightClickEdit();
    }
  }
}

// 创建全局实例
window.AppRenderer = new AppRenderer();


// ===== js/modules/ui-controller.js =====
/**
 * UI 控制器模块
 * 负责模态框、应用切换和整体UI交互
 */
class UIController {
  constructor() {
    this.configManager = window.ConfigManager;
    this.appRenderer = window.AppRenderer;
    this.init();
  }

  init() {
    // 等待DOM加载完成
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAll());
    } else {
      this.setupAll();
    }
  }

  setupAll() {
    try {
      this.setupModalEvents();
      this.setupAppToggle();
      this.setupEngineDisplay();
      this.setupSearchFunction();
      const iconPickerToggle = DOM.find('#iconPickerToggle');
      if (iconPickerToggle) {
        DOM.on(iconPickerToggle, 'click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.showIconSelector();
        });
      }
      console.log('✅ UI Controller 初始化完成');
    } catch (error) {
      console.error('❌ UI Controller 初始化失败:', error);
    }
  }

  /**
   * 设置模态框事件
   */
  setupModalEvents() {
    const modal = DOM.find('#editModal');
    if (!modal) return;

    // 关闭按钮
    const closeBtn = DOM.find('.edit-modal-close');
    if (closeBtn) {
      DOM.on(closeBtn, 'click', () => this.hideEditModal());
    }

    // 取消按钮
    const cancelBtn = DOM.find('.btn-cancel');
    if (cancelBtn) {
      DOM.on(cancelBtn, 'click', () => this.hideEditModal());
    }

    // 表单提交
    const form = DOM.find('#editForm');
    if (form) {
      DOM.on(form, 'submit', (e) => {
        e.preventDefault();
        this.saveEditedApp();
      });
    }

    // 点击背景关闭
    DOM.on(modal, 'click', (e) => {
      if (e.target === modal) {
        this.hideEditModal();
      }
    });

    // ESC 键关闭
    DOM.on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && modal.style.display === 'flex') {
        this.hideEditModal();
      }
    });
  }

  /**
   * 设置应用切换
   */
  setupAppToggle() {
    const leftBtn = DOM.find('.switch-btn-left');
    const rightBtn = DOM.find('.switch-btn-right');
    const externalContainer = DOM.find('#app');
    const internalContainer = DOM.find('#app1');

    if (!leftBtn || !rightBtn || !externalContainer || !internalContainer) {
      return;
    }

    // 加载保存的视图状态
    const savedView = Helpers.Storage.get('homedock-app-view', 'external');
    if (savedView === 'internal') {
      leftBtn.classList.remove('active');
      rightBtn.classList.add('active');
      // ✅ 更新 ARIA 状态
      leftBtn.setAttribute('aria-pressed', 'false');
      rightBtn.setAttribute('aria-pressed', 'true');
    } else {
      leftBtn.classList.add('active');
      rightBtn.classList.remove('active');
      // ✅ 更新 ARIA 状态
      leftBtn.setAttribute('aria-pressed', 'true');
      rightBtn.setAttribute('aria-pressed', 'false');
    }

    this.updateAppView(savedView === 'internal', externalContainer, internalContainer);

    // 切换事件
    DOM.on(leftBtn, 'click', () => {
      leftBtn.classList.add('active');
      rightBtn.classList.remove('active');
      // ✅ 更新 ARIA 状态
      leftBtn.setAttribute('aria-pressed', 'true');
      rightBtn.setAttribute('aria-pressed', 'false');
      Helpers.Storage.set('homedock-app-view', 'external');
      this.updateAppView(false, externalContainer, internalContainer);
    });

    DOM.on(rightBtn, 'click', () => {
      rightBtn.classList.add('active');
      leftBtn.classList.remove('active');
      // ✅ 更新 ARIA 状态
      rightBtn.setAttribute('aria-pressed', 'true');
      leftBtn.setAttribute('aria-pressed', 'false');
      Helpers.Storage.set('homedock-app-view', 'internal');
      this.updateAppView(true, externalContainer, internalContainer);
    });
  }

  /**
   * 更新应用视图
   */
  updateAppView(isInternal, externalContainer, internalContainer) {
    if (isInternal) {
      DOM.hide(externalContainer);
      DOM.show(internalContainer);
    } else {
      DOM.show(externalContainer);
      DOM.hide(internalContainer);
    }
  }

  /**
   * 设置搜索引擎显示
   */
  setupEngineDisplay() {
    const searchEngine = DOM.find('#search-engine');
    const currentEngineLabel = DOM.find('#current-engine-label');
    const searchBox = DOM.find('#search-box');
    const engineSelector = DOM.find('#search-engine-selector');
    const dropdown = DOM.find('#search-engine-dropdown');
    const dropdownItems = DOM.findAll('.dropdown-item');

    if (!searchEngine || !currentEngineLabel || !searchBox || !engineSelector || !dropdown) {
      return;
    }

    // 加载保存的搜索引擎，默认为百度
    const savedEngine = Helpers.Storage.get('homedock-search-engine', 'baidu');
    searchEngine.value = savedEngine;

    // 更新显示
    this.updateEngineDisplay(savedEngine);

    // 引擎选择器点击事件
    DOM.on(engineSelector, 'click', (e) => {
      e.stopPropagation();
      this.toggleEngineDropdown();
    });

    // 下拉项点击事件
    dropdownItems.forEach(item => {
      DOM.on(item, 'click', (e) => {
        e.stopPropagation();
        const engine = item.dataset.engine;
        if (searchEngine.value !== engine) {
          searchEngine.value = engine;
          Helpers.Storage.set('homedock-search-engine', engine);
          this.updateEngineDisplay(engine);
        }
        this.hideEngineDropdown();
      });
    });

    // 点击其他地方关闭下拉菜单
    DOM.on(document, 'click', (e) => {
      if (!e.target.closest('#search-engine-selector') && !e.target.closest('.search-engine-dropdown')) {
        this.hideEngineDropdown();
      }
    });
  }

  toggleEngineDropdown() {
    const engineSelector = DOM.find('#search-engine-selector');
    const dropdown = DOM.find('#search-engine-dropdown');

    if (dropdown.classList.contains('show')) {
      this.hideEngineDropdown();
    } else {
      this.showEngineDropdown();
    }
  }

  showEngineDropdown() {
    const engineSelector = DOM.find('#search-engine-selector');
    const dropdown = DOM.find('#search-engine-dropdown');

    engineSelector.classList.add('open');
    dropdown.classList.add('show');
  }

  hideEngineDropdown() {
    const engineSelector = DOM.find('#search-engine-selector');
    const dropdown = DOM.find('#search-engine-dropdown');

    engineSelector.classList.remove('open');
    dropdown.classList.remove('show');
  }

  /**
   * 更新搜索引擎显示
   */
  updateEngineDisplay(engine) {
    const currentEngineLabel = DOM.find('#current-engine-label');
    const searchEngineLogo = DOM.find('#search-engine-logo');
    const searchBox = DOM.find('#search-box');

    let iconSrc = '';
    let altText = '';
    let labelText = '';

    switch (engine) {
      case 'google':
        iconSrc = 'img/google-official.svg';
        altText = 'Google';
        labelText = 'Google';
        break;
      case 'bing':
        iconSrc = 'img/Bing.svg';
        altText = 'Bing';
        labelText = 'Bing';
        break;
      case 'baidu':
        iconSrc = 'img/baidu.svg';
        altText = 'Baidu';
        labelText = '百度';
        break;
    }

    // 更新搜索框上方的logo图标
    if (searchEngineLogo) {
      searchEngineLogo.src = iconSrc;
      searchEngineLogo.alt = altText;
    }

    if (currentEngineLabel) {
      currentEngineLabel.textContent = labelText;
    }

    // 更新样式
    searchBox.classList.remove('engine-google', 'engine-bing', 'engine-baidu');
    searchBox.classList.add(`engine-${engine}`);
  }

  /**
   * 打开添加应用对话框
   */
  openAddApp(targetType) {
    const modal = DOM.find('#editModal');
    if (!modal) return;

    // 清空表单
    DOM.find('#appName').value = '';
    DOM.find('#externalUrl').value = targetType === 'external' ? '' : '';
    DOM.find('#internalUrl').value = targetType === 'internal' ? '' : '';
    DOM.find('#iconPath').value = '';

    // 设置模式
    modal.dataset.appIndex = targetType === 'external' ? 'new-external' : 'new-internal';

    // 显示模态框
    DOM.show(modal);
    modal.style.display = 'flex';

    // 聚焦到名称输入框
    setTimeout(() => {
      DOM.find('#appName').focus();
    }, 100);
  }

  /**
   * 编辑应用
   */
  async editApp(appIndex) {
    const config = this.configManager.getCurrentConfig();
    const app = config.applications[appIndex];

    if (!app) {
      NotificationUtils.showError('应用不存在');
      return;
    }

    // 填充表单
    DOM.find('#appName').value = app.name || '';
    DOM.find('#externalUrl').value = app.external_url || '';
    DOM.find('#internalUrl').value = app.internal_url || '';
    DOM.find('#iconPath').value = app.icon || '';

    // 显示模态框
    const modal = DOM.find('#editModal');
    modal.dataset.appIndex = appIndex;
    DOM.show(modal);
    modal.style.display = 'flex';

    // 聚焦到名称输入框
    setTimeout(() => {
      DOM.find('#appName').focus();
    }, 100);
  }

  /**
   * 保存编辑的应用
   */
  async saveEditedApp() {
    const modal = DOM.find('#editModal');
    const appIndexValue = modal.dataset.appIndex;

    // 获取表单数据
    const formData = new FormData(DOM.find('#editForm'));
    const appData = {
      name: formData.get('appName')?.trim(),
      external_url: formData.get('externalUrl')?.trim(),
      internal_url: formData.get('internalUrl')?.trim(),
      icon: formData.get('iconPath')?.trim()
    };

    // 验证必填字段
    if (!appData.name) {
      NotificationUtils.showError('请输入应用名称');
      return;
    }

    // 验证至少有一个URL
    if (!appData.external_url && !appData.internal_url) {
      NotificationUtils.showError('请至少输入一个URL地址');
      return;
    }

    // 验证URL格式
    if (appData.external_url && !this.validateUrl(appData.external_url)) {
      NotificationUtils.showError('外部URL格式不正确');
      return;
    }

    if (appData.internal_url && !this.validateUrl(appData.internal_url)) {
      NotificationUtils.showError('内部URL格式不正确');
      return;
    }

    try {
      if (appIndexValue === 'new-external' || appIndexValue === 'new-internal') {
        // 新增应用
        await this.configManager.addApplication(appData);
        NotificationUtils.showSuccess('应用添加成功');
      } else {
        // 更新应用
        const appIndex = parseInt(appIndexValue, 10);
        await this.configManager.updateApplication(appIndex, appData);
        NotificationUtils.showSuccess('应用更新成功');
      }

      this.hideEditModal();
      this.appRenderer.refresh();

    } catch (error) {
      console.error('保存应用失败:', error);
      NotificationUtils.showError('保存失败: ' + error.message);
    }
  }

  /**
   * 删除应用
   */
  async deleteApp(appIndex) {
    if (!confirm('确定要删除这个应用吗？')) {
      return;
    }

    try {
      await this.configManager.deleteApplication(appIndex);
      NotificationUtils.showSuccess('应用已删除');
      this.appRenderer.refresh();
    } catch (error) {
      console.error('删除应用失败:', error);
      NotificationUtils.showError('删除失败: ' + error.message);
    }
  }

  /**
   * 验证URL格式
   */
  validateUrl(url) {
    if (!url || url === '#') {
      return true; // 允许锚链接
    }

    try {
      new URL(url);
      return true;
    } catch {
      // 尝试添加协议
      try {
        new URL('http://' + url);
        return true;
      } catch {
        return false;
      }
    }
  }

  /**
   * 隐藏编辑模态框
   */
  hideEditModal() {
    const modal = DOM.find('#editModal');
    if (modal) {
      modal.style.display = 'none';
    }
  }

  /**
   * 显示图标选择器
   */
  showIconSelector() {
    const picker = DOM.find('#iconPicker');
    const grid = DOM.find('#iconPickerGrid');
    const input = DOM.find('#iconPath');

    if (!picker || !grid || !input) {
      return;
    }

    if (picker.dataset.initialized !== 'true') {
      const presetIcons = window.PRESET_ICONS || [];

      grid.innerHTML = '';

      presetIcons.forEach(icon => {
        const item = DOM.create('div', {
          className: 'icon-picker-item'
        }, [
          DOM.create('img', {
            src: icon.path,
            alt: icon.name
          }),
          DOM.create('span', {}, [icon.name])
        ]);

        DOM.on(item, 'click', () => {
          input.value = icon.path;
          input.focus();
          picker.style.display = 'none';
        });

        grid.appendChild(item);
      });

      picker.dataset.initialized = 'true';
    }

    if (picker.style.display === 'block') {
      picker.style.display = 'none';
    } else {
      picker.style.display = 'block';
    }
  }

  /**
   * 设置搜索功能
   */
  setupSearchFunction() {
    const searchInput = DOM.find('#search-input');
    if (!searchInput) return;

    // 回车搜索
    DOM.on(searchInput, 'keypress', (e) => {
      if (e.key === 'Enter') {
        this.performSearch();
      }
    });

    // 自动完成功能
    this.setupSearchAutocomplete(searchInput);
  }

  /**
   * 设置搜索自动完成
   */
  setupSearchAutocomplete(searchInput) {
    const SEARCH_HISTORY_KEY = 'homedock-search-history';
    let autocompleteTimeout;

    DOM.on(searchInput, 'input', (e) => {
      clearTimeout(autocompleteTimeout);
      const query = e.target.value.trim();

      if (query.length < 2) {
        this.hideAutocomplete();
        return;
      }

      autocompleteTimeout = setTimeout(() => {
        this.showAutocomplete(query, SEARCH_HISTORY_KEY);
      }, 200);
    });

    // 点击其他地方隐藏自动完成
    DOM.on(document, 'click', (e) => {
      if (!e.target.closest('.search-autocomplete') && e.target !== searchInput) {
        this.hideAutocomplete();
      }
    });
  }

  /**
   * 显示自动完成
   */
  showAutocomplete(query, historyKey) {
    const history = Helpers.Storage.get(historyKey, []);
    const filtered = history.filter(item =>
      item.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5);

    if (filtered.length === 0) {
      this.hideAutocomplete();
      return;
    }

    const searchInput = DOM.find('#search-input');
    const autocomplete = DOM.create('div', {
      className: 'search-autocomplete'
    });

    filtered.forEach(item => {
      const suggestion = DOM.create('div', {
        className: 'search-suggestion',
        onclick: () => {
          searchInput.value = item;
          this.performSearch();
          this.hideAutocomplete();
        }
      }, [Helpers.escapeHtml(item)]);

      autocomplete.appendChild(suggestion);
    });

    // 插入到搜索框后面
    const searchBox = DOM.find('#search-box');
    const existing = DOM.find('.search-autocomplete');
    if (existing) {
      searchBox.removeChild(existing);
    }

    searchBox.appendChild(autocomplete);
  }

  /**
   * 隐藏自动完成
   */
  hideAutocomplete() {
    const autocomplete = DOM.find('.search-autocomplete');
    if (autocomplete) {
      autocomplete.parentNode.removeChild(autocomplete);
    }
  }

  /**
   * 执行搜索
   */
  performSearch() {
    const searchInput = DOM.find('#search-input');
    const searchEngine = DOM.find('#search-engine');
    const query = searchInput.value.trim();

    if (!query) {
      NotificationUtils.showError('请输入搜索内容');
      return;
    }

    // 保存搜索历史
    const SEARCH_HISTORY_KEY = 'homedock-search-history';
    let history = Helpers.Storage.get(SEARCH_HISTORY_KEY, []);
    history = history.filter(item => item !== query);
    history.unshift(query);
    history = history.slice(0, 10);
    Helpers.Storage.set(SEARCH_HISTORY_KEY, history);

    // 执行搜索
    const currentEngine = searchEngine.value;
    let searchUrl;

    switch (currentEngine) {
      case 'google':
        searchUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
        break;
      case 'bing':
        searchUrl = `https://cn.bing.com/search?q=${encodeURIComponent(query)}`;
        break;
      case 'baidu':
        searchUrl = `https://www.baidu.com/s?wd=${encodeURIComponent(query)}`;
        break;
      default:
        searchUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
    }

    window.open(searchUrl, '_blank');
  }
}

// 创建全局实例
window.UIManager = new UIController();


// ===== js/modules/search-engine.js =====
/**
 * 搜索引擎模块
 * 负责搜索功能和背景管理
 */
class SearchEngine {
  constructor() {
    this.configManager = window.ConfigManager;
    this.init();
  }

  init() {
    this.setupBackgroundManager();
    this.setupSearchShortcuts();
  }

  /**
   * 设置背景管理
   */
  setupBackgroundManager() {
    const changeWallpaperBtn = DOM.find('#change-wallpaper-btn');
    if (changeWallpaperBtn) {
      DOM.on(changeWallpaperBtn, 'click', () => {
        this.changeWallpaper();
      });
    }
  }

  /**
   * 应用背景配置
   */
  applyBackgroundFromConfig(forceRefreshWallpaper = false) {
    const config = this.configManager.getCurrentConfig();
    if (!config) {
      this.setupBingBackground();
      this.applyBackgroundBlur('light');
      return;
    }

    const changeWallpaperBtn = DOM.find('#change-wallpaper-btn');
    const bg = config.background;

    if (!bg || !bg.mode) {
      if (changeWallpaperBtn) DOM.show(changeWallpaperBtn);
      this.setupBingBackground();
      this.applyBackgroundBlur('light');
      return;
    }

    // 应用背景模糊度
    this.applyBackgroundBlur(bg.blur || 'light');

    switch (bg.mode) {
      case 'wallpaper':
        if (changeWallpaperBtn) DOM.show(changeWallpaperBtn);
        this.setupBingBackground(forceRefreshWallpaper);
        break;

      case 'solid':
        if (changeWallpaperBtn) DOM.hide(changeWallpaperBtn);
        this.applySolidBackground(bg.solidColor || '#202124');
        break;

      case 'gradient':
        if (changeWallpaperBtn) DOM.hide(changeWallpaperBtn);
        this.applyGradientBackground(
          bg.gradientFrom || '#141e30',
          bg.gradientTo || '#243b55'
        );
        break;

      default:
        if (changeWallpaperBtn) DOM.show(changeWallpaperBtn);
        this.setupBingBackground();
    }
  }

  /**
   * 设置必应壁纸
   */
  setupBingBackground(forceRefresh = false) {
    const timestamp = forceRefresh ? Date.now() : '';
    const imageUrl = this.resolveWallpaperUrl(`ts=${timestamp}`);

    console.log('🖼️ Setting up Bing wallpaper:', imageUrl);

    // 🚀 性能优化：立即设置基础背景色，避免白屏
    document.body.style.backgroundColor = '#000000';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundPosition = 'center center';
    document.body.style.backgroundAttachment = 'fixed';
    // 拉伸图片填满整个屏幕
    document.body.style.backgroundSize = '100% 100%';

    // 异步加载壁纸图片
    const img = new Image();

    img.onload = () => {
      console.log('✅ Bing wallpaper loaded successfully');
      // 图片加载完成后替换背景
      document.body.style.backgroundImage =
        `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), url('${imageUrl}')`;
      // 拉伸图片填满整个屏幕
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundSize = '100% 100%';
      document.body.style.backgroundPosition = 'center center';
      document.body.style.backgroundAttachment = 'fixed';
    };

    img.onerror = (error) => {
      console.warn('❌ Failed to load Bing wallpaper, using fallback:', error);
      this.applyGradientBackground('#141e30', '#243b55');
    };

    // 添加超时处理
    img.addEventListener('error', () => {
      console.warn('⏰ Bing wallpaper load timeout');
      this.applyGradientBackground('#141e30', '#243b55');
    }, { once: true });

    // 设置超时
    setTimeout(() => {
      if (!img.complete) {
        img.src = ''; // 取消加载
        img.onerror(new Error('Timeout'));
      }
    }, 10000); // 10秒超时

    // 开始异步加载
    img.src = imageUrl;
  }

  /**
   * 应用纯色背景
   */
  applySolidBackground(color) {
    document.body.style.backgroundImage = 'none';
    document.body.style.backgroundColor = color;
    document.body.style.backgroundRepeat = 'repeat';
    document.body.style.backgroundSize = 'auto';
    document.body.style.backgroundPosition = 'top left';
    document.body.style.backgroundAttachment = 'scroll';
  }

  /**
   * 应用渐变背景
   */
  applyGradientBackground(fromColor, toColor) {
    document.body.style.backgroundImage =
      `linear-gradient(135deg, ${fromColor}, ${toColor})`;
    document.body.style.backgroundColor = '#000000';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center center';
    document.body.style.backgroundAttachment = 'fixed';
  }

  /**
   * 更换壁纸
   */
  async changeWallpaper() {
    try {
      this.applyBackgroundFromConfig(true);
    } catch (error) {
      console.error('Failed to change wallpaper:', error);
    }
  }

  /**
   * 设置搜索快捷键
   */
  setupSearchShortcuts() {
    DOM.on(document, 'keydown', (e) => {
      // Ctrl/Cmd + K 聚焦搜索框
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = DOM.find('#search-input');
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }

      // ESC 清空搜索框
      if (e.key === 'Escape') {
        const searchInput = DOM.find('#search-input');
        if (searchInput === document.activeElement) {
          searchInput.value = '';
          searchInput.blur();
          this.hideAutocomplete();
        }
      }
    });

    // 搜索框焦点效果
    const searchInput = DOM.find('#search-input');
    if (searchInput) {
      DOM.on(searchInput, 'focus', () => {
        searchInput.parentElement.classList.add('search-focused');
      });

      DOM.on(searchInput, 'blur', () => {
        searchInput.parentElement.classList.remove('search-focused');
      });
    }
  }

  /**
   * 搜索建议功能
   */
  async getSearchSuggestions(query) {
    if (query.length < 2) return [];

    try {
      // 这里可以集成实际的搜索建议API
      // 例如 Google Suggest API 或其他服务
      const suggestions = await this.fetchGoogleSuggestions(query);
      return suggestions;
    } catch (error) {
      console.warn('Failed to fetch search suggestions:', error);
      return [];
    }
  }

  /**
   * 获取Google搜索建议
   */
  async fetchGoogleSuggestions(query) {
    try {
      const response = await fetch(
        `https://suggestqueries.google.com/complete/search?client=firefox&q=${encodeURIComponent(query)}`,
        { mode: 'cors' }
      );

      // 由于CORS限制，这里可能需要使用代理或其他方法
      // 暂时返回空数组
      return [];
    } catch (error) {
      return [];
    }
  }

  /**
   * 隐藏自动完成
   */
  hideAutocomplete() {
    // 由UIController处理
    const autocomplete = DOM.find('.search-autocomplete');
    if (autocomplete) {
      autocomplete.parentNode.removeChild(autocomplete);
    }
  }

  /**
   * 预加载下一张壁纸
   */
  preloadNextWallpaper() {
    try {
      const preloadImg = new Image();
      preloadImg.onload = () => {
        console.log('✅ 壁纸预加载成功');
      };
      preloadImg.onerror = () => {
        console.warn('⚠️ 壁纸预加载失败');
      };
      preloadImg.src = this.resolveWallpaperUrl(
        'preload=true&ts=' + Date.now()
      );
    } catch (error) {
      console.warn('⚠️ 壁纸预加载出错:', error);
    }
  }

  resolveWallpaperUrl(query) {
    try {
      const { origin, hostname } = window.location || {};
      if (hostname && hostname.endsWith('.pages.dev')) {
        return `https://homedock.piaozhitian.workers.dev/bing-wallpaper?${query}`;
      }
      if (origin && origin.startsWith('http')) {
        return origin.replace(/\/+$/, '') + `/bing-wallpaper?${query}`;
      }
      return `/bing-wallpaper?${query}`;
    } catch (e) {
      console.warn('壁纸地址解析失败，回退到相对路径', e);
      return `/bing-wallpaper?${query}`;
    }
  }

  /**
   * 设置壁纸定时切换
   */
  setupWallpaperAutoChange(interval = 3600000) {
    const autoChangeKey = 'homedock-auto-change-wallpaper';
    const enabled = Helpers.Storage.get(autoChangeKey, false);

    if (enabled) {
      setInterval(() => {
        this.changeWallpaper();
      }, interval);
    }
  }

  /**
   * 背景性能优化
   */
  optimizeBackgroundPerformance() {
    document.body.style.willChange = 'background-image';

    const isLowPerformance = this.detectLowPerformanceDevice();

    if (isLowPerformance) {
      const config = this.configManager.getCurrentConfig();
      if (config && config.background && config.background.mode === 'wallpaper') {
        this.applySolidBackground('#202124');
      }
    }
  }

  /**
   * 检测低性能设备
   */
  detectLowPerformanceDevice() {
    const memory = navigator.deviceMemory || 4;
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    return (
      memory < 2 ||
      (connection &&
        connection.effectiveType &&
        ['slow-2g', '2g', '3g'].includes(connection.effectiveType))
    );
  }

  /**
   * 设置背景缓存策略
   */
  setupBackgroundCaching() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('message', (event) => {
          if (event.data.type === 'CACHE_UPDATED') {
            console.log('Background cache updated');
          }
        });
      });
    }
  }

  /**
   * 应用背景模糊效果
   */
  applyBackgroundBlur(blurLevel) {
    const body = document.body;

    body.classList.remove(
      'bg-blur-none',
      'bg-blur-light',
      'bg-blur-medium',
      'bg-blur-heavy'
    );

    switch (blurLevel) {
      case 'none':
        body.classList.add('bg-blur-none');
        break;
      case 'light':
        body.classList.add('bg-blur-light');
        break;
      case 'medium':
        body.classList.add('bg-blur-medium');
        break;
      case 'heavy':
        body.classList.add('bg-blur-heavy');
        break;
      default:
        body.classList.add('bg-blur-light');
        break;
    }

    console.log('🎨 Applied background blur level:', blurLevel);
  }
}

// 创建全局实例
window.SearchEngine = new SearchEngine();


// ===== js/preset-icons.js =====
if (!window.PRESET_ICONS) {
  window.PRESET_ICONS = [
    { path: 'img/png/Edge.svg', name: 'Edge' },
    { path: 'img/png/Firefox.svg', name: 'Firefox' },
    { path: 'img/png/Adminer.svg', name: 'Adminer' },
    { path: 'img/png/Ai.svg', name: 'Ai' },
    { path: 'img/png/Alist.svg', name: 'Alist' },
    { path: 'img/png/Aria2.svg', name: 'Aria2' },
    { path: 'img/png/Book.svg', name: 'Book' },
    { path: 'img/png/Calibre.png', name: 'Calibre' },
    { path: 'img/png/Chatgpt.png', name: 'ChatGPT' },
    { path: 'img/png/Cloud.svg', name: 'Cloud' },
    { path: 'img/png/Downloads.png', name: 'Downloads' },
    { path: 'img/png/Emby.svg', name: 'Emby' },
    { path: 'img/png/FaceTime.png', name: 'FaceTime' },
    { path: 'img/png/File.svg', name: 'File' },
    { path: 'img/png/FileBrowser.svg', name: 'FileBrowser' },
    { path: 'img/png/FileSync.svg', name: 'FileSync' },
    { path: 'img/png/HPE.svg', name: 'HPE' },
    { path: 'img/png/Homarr.png', name: 'Homarr' },
    { path: 'img/png/Jellyfin.svg', name: 'Jellyfin' },
    { path: 'img/png/KMS.svg', name: 'KMS' },
    { path: 'img/png/Lucky.svg', name: 'Lucky' },
    { path: 'img/png/MariaDB.svg', name: 'MariaDB' },
    { path: 'img/png/MovieTool.svg', name: 'MovieTool' },
    { path: 'img/png/Music.svg', name: 'Music' },
    { path: 'img/png/MyAdmin.svg', name: 'MyAdmin' },
    { path: 'img/png/NasTool.svg', name: 'NasTool' },
    { path: 'img/png/Openwrt.svg', name: 'OpenWrt' },
    { path: 'img/png/PVE.svg', name: 'PVE' },
    { path: 'img/png/Photos.svg', name: 'Photos' },
    { path: 'img/png/Podcats.png', name: 'Podcasts' },
    { path: 'img/png/Portainer.svg', name: 'Portainer' },
    { path: 'img/png/Print.svg', name: 'Print' },
    { path: 'img/png/Qb.svg', name: 'Qbittorrent' },
    { path: 'img/png/QingLong.svg', name: 'QingLong' },
    { path: 'img/png/S-PDF.svg', name: 'S-PDF' },
    { path: 'img/png/Sam.svg', name: 'Sam' },
    { path: 'img/png/Server.svg', name: 'Server' },
    { path: 'img/png/Settings.png', name: 'Settings' },
    { path: 'img/png/Thunder.png', name: 'Thunder' },
    { path: 'img/png/Transmission.png', name: 'Transmission' },
    { path: 'img/png/Unraid.svg', name: 'Unraid' },
    { path: 'img/png/VM.svg', name: 'VM' },
    { path: 'img/png/Videos.svg', name: 'Videos' },
    { path: 'img/png/XunLei.svg', name: 'XunLei' },
    { path: 'img/png/iCloud-Drive.png', name: 'iCloud Drive' },
    { path: 'img/png/nginxWebUI.png', name: 'nginxWebUI' },
    { path: 'img/png/favicon.svg', name: 'Favicon' },
    { path: 'img/png/favicon2.svg', name: 'Favicon2' },
    { path: 'img/png/logo.svg', name: 'Logo' },
    { path: 'img/png/猫logo2.svg', name: '猫Logo' },
    { path: 'img/png/DPanel.svg', name: 'DPanel' },
    { path: 'img/png/AdGuard.svg', name: 'AdGuard' },
    { path: 'img/png/Esxi.svg', name: 'Esxi' }
  ];
}


// ===== js/utils/service-worker.js =====
/**
 * Service Worker 注册器
 * 负责注册、更新和管理Service Worker
 */
class ServiceWorkerManager {
  constructor() {
    this.swUrl = '/sw.js';
    this.isSupported = 'serviceWorker' in navigator;
    this.controller = null;
    this.updateAvailable = false;
    this.init();
  }

  init() {
    if (!this.isSupported) {
      console.warn('Service Worker not supported');
      return;
    }

    // 注册Service Worker
    this.registerServiceWorker();

    // 监听Service Worker消息
    this.setupMessageListener();

    // 检查更新
    this.checkForUpdates();
  }

  /**
   * 注册Service Worker
   */
  async registerServiceWorker() {
    try {
      const registration = await navigator.serviceWorker.register(this.swUrl, {
        scope: '/'
      });

      console.log('Service Worker registered successfully:', registration);

      // 监听更新
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // 有新版本可用
            this.updateAvailable = true;
            this.showUpdateNotification();
          }
        });
      });

      // 获取当前控制的Service Worker
      if (navigator.serviceWorker.controller) {
        this.controller = navigator.serviceWorker.controller;
      }

      // 监听控制器变化
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        this.controller = navigator.serviceWorker.controller;
        window.location.reload(); // 重新加载页面以应用新版本
      });

    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }

  /**
   * 设置消息监听器
   */
  setupMessageListener() {
    const channel = new MessageChannel();

    // 监听来自Service Worker的消息
    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type, data } = event.data;

      switch (type) {
        case 'CACHE_UPDATED':
          console.log('Cache updated:', data);
          break;

        case 'SYNC_COMPLETED':
          console.log('Background sync completed');
          break;

        default:
          console.log('Service Worker message:', type, data);
      }
    });
  }

  /**
   * 检查更新
   */
  async checkForUpdates() {
    if (!navigator.serviceWorker.controller) {
      return;
    }

    try {
      // 向Service Worker发送获取版本的消息
      const channel = new MessageChannel();

      channel.port1.onmessage = (event) => {
        const { version } = event.data;
        console.log('Service Worker version:', version);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_VERSION' },
        [channel.port2]
      );

    } catch (error) {
      console.error('Failed to check for updates:', error);
    }
  }

  /**
   * 显示更新通知
   */
  showUpdateNotification() {
    NotificationUtils.showSuccess('发现新版本，点击刷新按钮更新', 8000);

    // 添加更新按钮到页面
    this.addUpdateButton();
  }

  /**
   * 添加更新按钮
   */
  addUpdateButton() {
    // 检查是否已存在更新按钮
    if (DOM.find('#update-button')) {
      return;
    }

    const updateBtn = DOM.create('button', {
      id: 'update-button',
      className: 'update-button',
      innerHTML: '🔄 更新',
      onclick: () => this.applyUpdate()
    });

    // 添加到页面
    const container = DOM.find('#kg-btn') || DOM.find('#wrap');
    if (container) {
      container.appendChild(updateBtn);
    }

    // 添加样式
    this.addUpdateButtonStyles();
  }

  /**
   * 应用更新
   */
  async applyUpdate() {
    if (!this.updateAvailable) {
      return;
    }

    try {
      // 通知Service Worker跳过等待
      const channel = new MessageChannel();

      navigator.serviceWorker.controller.postMessage(
        { type: 'SKIP_WAITING' },
        [channel.port2]
      );

      NotificationUtils.showSuccess('正在更新应用...');

      // 显示加载指示器
      this.showLoadingOverlay();

    } catch (error) {
      console.error('Failed to apply update:', error);
      NotificationUtils.showError('更新失败，请手动刷新页面');
    }
  }

  /**
   * 显示加载遮罩
   */
  showLoadingOverlay() {
    const overlay = DOM.create('div', {
      id: 'update-loading-overlay',
      className: 'update-loading-overlay'
    }, [
      DOM.create('div', {
        className: 'update-loading-content'
      }, [
        DOM.create('div', { className: 'update-loading-spinner' }),
        DOM.create('p', {}, ['正在更新应用...'])
      ])
    ]);

    document.body.appendChild(overlay);
  }

  /**
   * 添加更新按钮样式
   */
  addUpdateButtonStyles() {
    if (document.getElementById('update-button-styles')) {
      return;
    }

    const styles = DOM.create('style', {
      id: 'update-button-styles'
    });

    styles.textContent = `
      .update-button {
        background: linear-gradient(135deg, #28a745, #20c997);
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 12px;
        cursor: pointer;
        margin-left: 10px;
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
      }

      .update-button:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
      }

      .update-loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 99999;
        backdrop-filter: blur(5px);
      }

      .update-loading-content {
        text-align: center;
        color: white;
      }

      .update-loading-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        border-top-color: white;
        animation: update-spin 1s ease-in-out infinite;
        margin: 0 auto 20px;
      }

      @keyframes update-spin {
        to { transform: rotate(360deg); }
      }
    `;

    document.head.appendChild(styles);
  }

  /**
   * 清除缓存
   */
  async clearCache() {
    if (!navigator.serviceWorker.controller) {
      NotificationUtils.showError('Service Worker未激活');
      return;
    }

    try {
      const channel = new MessageChannel();

      channel.port1.onmessage = (event) => {
        const { success } = event.data;
        if (success) {
          NotificationUtils.showSuccess('缓存已清除');
        } else {
          NotificationUtils.showError('清除缓存失败');
        }
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_CACHE' },
        [channel.port2]
      );

    } catch (error) {
      console.error('Failed to clear cache:', error);
      NotificationUtils.showError('清除缓存失败');
    }
  }

  /**
   * 获取缓存统计
   */
  async getCacheStats() {
    if (!navigator.serviceWorker.controller) {
      return null;
    }

    try {
      const channel = new MessageChannel();

      return new Promise((resolve) => {
        channel.port1.onmessage = (event) => {
          resolve(event.data.stats);
        };

        navigator.serviceWorker.controller.postMessage(
          { type: 'GET_CACHE_STATS' },
          [channel.port2]
        );
      });

    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return null;
    }
  }

  /**
   * 预加载图片
   */
  async preloadImages(urls) {
    if (!navigator.serviceWorker.controller || !urls.length) {
      return;
    }

    try {
      const channel = new MessageChannel();

      channel.port1.onmessage = (event) => {
        const { success } = event.data;
        if (success) {
          console.log('Images preloaded successfully');
        }
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'PRELOAD_IMAGES', data: { urls } },
        [channel.port2]
      );

    } catch (error) {
      console.error('Failed to preload images:', error);
    }
  }
}

// 创建全局实例
window.ServiceWorkerManager = new ServiceWorkerManager();

// ===== js/utils/image-optimizer.js =====
/**
 * 图片优化工具
 * 支持懒加载、WebP格式、预加载等功能
 */
class ImageOptimizer {
  constructor() {
    this.supportsWebP = this.checkWebPSupport();
    this.loadedImages = new Set();
    this.observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };
    this.init();
  }

  init() {
    // 初始化懒加载观察器
    this.initLazyLoading();

    // 预加载关键图片
    this.preloadCriticalImages();

    // 监听图片错误
    this.setupImageErrorHandling();
  }

  /**
   * 检查WebP支持
   */
  checkWebPSupport() {
    return Promise.resolve(false);
  }

  /**
   * 获取优化后的图片URL
   */
  async getOptimizedImageUrl(originalUrl, options = {}) {
    if (!originalUrl || originalUrl.startsWith('#')) {
      return originalUrl;
    }

    const {
      width,
      height,
      quality = 80,
      format = 'auto'
    } = options;

    // 如果是外部URL，直接返回
    if (originalUrl.startsWith('http')) {
      return originalUrl;
    }

    // 构建优化的URL
    let optimizedUrl = originalUrl;

    // 检查WebP支持
    if (format === 'auto') {
      const webPSupported = await this.supportsWebP;
      if (webPSupported && this.canConvertToWebP(originalUrl)) {
        optimizedUrl = originalUrl.replace(/\.(png|jpg|jpeg)$/i, '.webp');
      }
    } else if (format === 'webp') {
      optimizedUrl = originalUrl.replace(/\.(png|jpg|jpeg)$/i, '.webp');
    }

    // 添加尺寸参数（如果支持）
    if ((width || height) && this.supportsImageParams(optimizedUrl)) {
      const params = new URLSearchParams();
      if (width) params.append('w', width);
      if (height) params.append('h', height);
      if (quality !== 80) params.append('q', quality);

      const separator = optimizedUrl.includes('?') ? '&' : '?';
      optimizedUrl += separator + params.toString();
    }

    return optimizedUrl;
  }

  /**
   * 检查是否可以转换为WebP
   */
  canConvertToWebP(url) {
    const webpExtensions = ['.png', '.jpg', '.jpeg', '.gif'];
    return webpExtensions.some(ext => url.toLowerCase().endsWith(ext));
  }

  /**
   * 检查URL是否支持图片参数
   */
  supportsImageParams(url) {
    // 这里可以根据实际情况判断是否支持参数化URL
    // 例如：CDN服务、图片处理服务等
    return false;
  }

  /**
   * 初始化懒加载
   */
  initLazyLoading() {
    if (!('IntersectionObserver' in window)) {
      // 回退到立即加载
      this.loadAllImages();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, this.observerOptions);

    // 观察所有图片
    DOM.findAll('img[data-src]').forEach(img => {
      observer.observe(img);
    });
  }

  /**
   * 加载单个图片
   */
  async loadImage(img) {
    const src = img.dataset.src;
    if (!src || this.loadedImages.has(src)) {
      return;
    }

    try {
      // 添加加载状态
      img.classList.add('image-loading');

      // 获取优化后的URL
      const optimizedUrl = await this.getOptimizedImageUrl(src, {
        width: img.dataset.width,
        height: img.dataset.height
      });

      // 创建新图片进行预加载
      const newImg = new Image();

      newImg.onload = () => {
        img.src = optimizedUrl;
        img.classList.remove('image-loading');
        img.classList.add('image-loaded');
        this.loadedImages.add(src);

        // 淡入效果
        Animation.fadeIn(img, 300);
      };

      newImg.onerror = () => {
        // 回退到原始URL
        if (optimizedUrl !== src) {
          img.src = src;
          img.classList.remove('image-loading');
          img.classList.add('image-loaded');
          this.loadedImages.add(src);
        } else {
          img.classList.remove('image-loading');
          img.classList.add('image-error');
          this.handleImageError(img);
        }
      };

      newImg.src = optimizedUrl;

    } catch (error) {
      console.error('Failed to load image:', src, error);
      img.classList.remove('image-loading');
      img.classList.add('image-error');
      this.handleImageError(img);
    }
  }

  /**
   * 加载所有图片（不支持IntersectionObserver时的回退）
   */
  async loadAllImages() {
    const images = DOM.findAll('img[data-src]');

    for (const img of images) {
      await this.loadImage(img);
    }
  }

  /**
   * 预加载关键图片
   */
  async preloadCriticalImages() {
    const criticalImages = [
      'img/google-official.svg',
      'img/Bing.svg',
      'img/baidu.svg'
    ];

    for (const src of criticalImages) {
      try {
        const optimizedUrl = await this.getOptimizedImageUrl(src);
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = optimizedUrl;
        document.head.appendChild(link);
      } catch (error) {
        console.warn('Failed to preload critical image:', src);
      }
    }
  }

  /**
   * 处理图片错误
   */
  handleImageError(img) {
    // 设置默认图标
    if (!img.classList.contains('fallback-applied')) {
      img.src = 'img/png/favicon.svg';
      img.classList.add('fallback-applied');
      img.alt = '图标加载失败';
    }
  }

  /**
   * 设置图片错误处理
   */
  setupImageErrorHandling() {
    DOM.on(document, 'error', (e) => {
      if (e.target.tagName === 'IMG') {
        this.handleImageError(e.target);
      }
    }, true);
  }

  /**
   * 创建响应式图片元素
   */
  createResponsiveImage(src, alt, options = {}) {
    const {
      sizes = '100vw',
      breakpoints = [480, 768, 1024],
      lazy = true
    } = options;

    const picture = DOM.create('picture');

    // 为不同断点创建源
    if (breakpoints.length > 0) {
      for (let i = 0; i < breakpoints.length; i++) {
        const breakpoint = breakpoints[i];
        const width = breakpoint;

        const source = DOM.create('source', {
          'media': `(min-width: ${breakpoint}px)`,
          'sizes': sizes,
          'type': 'image/webp',
          'data-srcset': `${src}?w=${width}&format=webp ${width}w`
        });

        picture.appendChild(source);
      }
    }

    // 默认img元素
    const img = DOM.create('img', {
      'data-src': lazy ? src : src,
      'alt': alt,
      'loading': lazy ? 'lazy' : 'eager',
      'decoding': 'async'
    });

    if (lazy) {
      img.classList.add('lazy-image');
    }

    picture.appendChild(img);
    return picture;
  }

  /**
   * 批量优化应用图标
   */
  async optimizeAppIcons() {
    const appImages = DOM.findAll('.app-item img');

    for (const img of appImages) {
      if (img.src && !img.dataset.processed) {
        try {
          const optimizedUrl = await this.getOptimizedImageUrl(img.src, {
            width: 64,
            height: 64,
            quality: 75
          });

          if (optimizedUrl !== img.src) {
            // 创建新图片进行测试
            const testImg = new Image();
            testImg.onload = () => {
              img.src = optimizedUrl;
              img.dataset.processed = 'true';
            };
            testImg.src = optimizedUrl;
          } else {
            img.dataset.processed = 'true';
          }
        } catch (error) {
          console.warn('Failed to optimize app icon:', img.src);
          img.dataset.processed = 'true';
        }
      }
    }
  }

  /**
   * 图片缓存管理
   */
  clearImageCache() {
    this.loadedImages.clear();

    // 清除Service Worker缓存（如果有）
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        return Promise.all(
          cacheNames.filter(name => name.includes('image')).map(name => caches.delete(name))
        );
      });
    }
  }

  /**
   * 获取图片统计信息
   */
  getImageStats() {
    const totalImages = DOM.findAll('img').length;
    const loadedImages = DOM.findAll('img.image-loaded').length;
    const errorImages = DOM.findAll('img.image-error').length;
    const loadingImages = DOM.findAll('img.image-loading').length;

    return {
      total: totalImages,
      loaded: loadedImages,
      error: errorImages,
      loading: loadingImages,
      successRate: totalImages > 0 ? ((loadedImages / totalImages) * 100).toFixed(1) : 0
    };
  }
}

// 创建全局实例
window.ImageOptimizer = new ImageOptimizer();

